import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiArrowRight, FiCalendar, FiClock, FiShield, FiGift, FiLock } from 'react-icons/fi';
import { BaseModal } from './shared/BaseModal';

const ContentWrapper = styled.div`
  position: relative;
`;

const Header = styled.div`
  margin-bottom: 32px;
`;

const Title = styled.h2`
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 12px 0;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.5px;
  background: linear-gradient(135deg, #FFF, ${({ theme }) => theme.colors.primary});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 15px;
  line-height: 1.6;
  margin: 0;
`;

const StepList = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const StepConnector = styled.div`
  position: absolute;
  left: 20px;
  top: 40px;
  bottom: 20px;
  width: 2px;
  background: rgba(255, 255, 255, 0.1);
  z-index: 0;
`;

const Step = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 16px 0;
  position: relative;
  z-index: 1;
`;

const StepIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: ${({ theme, active }) => active ? theme.colors.gradient.primary : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${({ theme, active }) => active ? 'transparent' : 'rgba(255, 255, 255, 0.1)'};
  color: ${({ active }) => active ? 'white' : 'rgba(255, 255, 255, 0.5)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
  box-shadow: ${({ active, theme }) => active ? '0 4px 12px rgba(59, 130, 246, 0.3)' : 'none'};
`;

const StepContent = styled.div`
  flex: 1;
  padding-top: 4px;
`;

const StepTitle = styled.h4`
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 6px 0;
  font-size: 17px;
  font-weight: 600;
`;

const StepDescription = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  opacity: 0.8;
`;

const ActionButton = styled(motion.button)`
  width: 100%;
  padding: 16px;
  margin-top: 24px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.gradient.primary};
  color: white;
  border: none;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 16px;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
`;

const SecondaryActionButton = styled(motion.button)`
  width: 100%;
  padding: 14px;
  margin-top: 12px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  border: 1px solid rgba(255, 255, 255, 0.15);
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    border-color: ${({ theme }) => theme.colors.primary};
    background: rgba(255, 255, 255, 0.05);
  }
`;

export const OnboardingGuide = ({ isOpen, onClose, onOpenPreferences }) => {
    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            zIndex={2000}
            maxWidth="480px"
            showDecorativeElements={true}
            closeOnClickOutside={false}
        >
            <ContentWrapper>
                <Header>
                    <Title>Welcome to Visa Checker</Title>
                    <Subtitle>
                        Never miss a visa deadline. Track expiry dates with real-time reports and calendar reminders, completely free. No ads, no privacy concerns. Set up in three easy steps.
                    </Subtitle>
                </Header>

                <StepList>
                    <StepConnector />
                    <Step
                        initial={{ x: -10, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.2 }}
                    >
                        <StepIcon active><FiClock /></StepIcon>
                        <StepContent>
                            <StepTitle>Real-Time Countdown</StepTitle>
                            <StepDescription>Always know exactly how much time remains on your visa.</StepDescription>
                        </StepContent>
                    </Step>

                    <Step
                        initial={{ x: -10, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.3 }}
                    >
                        <StepIcon active><FiCalendar /></StepIcon>
                        <StepContent>
                            <StepTitle>Calendar Reminders</StepTitle>
                            <StepDescription>Get notified 5 days before your visa expires.</StepDescription>
                        </StepContent>
                    </Step>

                    <Step
                        initial={{ x: -10, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.4 }}
                    >
                        <StepIcon active><FiShield /></StepIcon>
                        <StepContent>
                            <StepTitle>Accurate Timezone Calculations</StepTitle>
                            <StepDescription>Precise calculations based on the issuing country's timezone.</StepDescription>
                        </StepContent>
                    </Step>

                    <Step
                        initial={{ x: -10, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.5 }}
                    >
                        <StepIcon active><FiGift /></StepIcon>
                        <StepContent>
                            <StepTitle>Free Usage</StepTitle>
                            <StepDescription>No subscriptions, no hidden fees.</StepDescription>
                        </StepContent>
                    </Step>

                    <Step
                        initial={{ x: -10, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.6 }}
                    >
                        <StepIcon active><FiLock /></StepIcon>
                        <StepContent>
                            <StepTitle>No Privacy Concerns</StepTitle>
                            <StepDescription>We don't store any private data.</StepDescription>
                        </StepContent>
                    </Step>
                </StepList>

                <ActionButton
                    onClick={onClose}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                >
                    Start Tracking <FiArrowRight />
                </ActionButton>

                <SecondaryActionButton
                    onClick={() => {
                        onClose();
                        if (onOpenPreferences) onOpenPreferences();
                    }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                >
                    Customize My Preferences
                </SecondaryActionButton>
            </ContentWrapper>
        </BaseModal>
    );
};
